import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { concatMap, from, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { event_status } from '../../../../../assets/constData/event_status';
import { EmptyStateComponent } from '../../../shared/empty-state/empty-state.component';
import { CapitalizeTitlePipe } from '../../../../shared/pipes/capitalize-title.pipe';
import {
  GeoPoint,
  MapViewport,
  centerForPoint,
  clampMapZoom,
  clusterPins,
  distanceKm,
  fitCenter,
  MapBounds,
  mapPinTone,
  queryBounds,
  mapScale,
  osmTileScale,
  osmTiles,
  pinScreenPosition,
  pointAtScreen,
  pointInBounds,
  viewportBounds,
  zoomToward,
} from './event-map-geo';
import { MappedEventPin, mapEventToPin } from './event-map-items';
import { nearMeCamera } from './event-near-me';
import { EventPlaceGeocodeService } from './event-place-geocode.service';

const DOUBLE_TAP_MS = 500;
const DOUBLE_TAP_SLOP_SQ = 28 * 28;
const SCROLL_ZOOM_DIVISOR = 1000;
const SCROLL_ZOOM_STEP_CAP = 0.1;
const PINCH_ZOOM_DIVISOR = 120;

import { SkeletonLoaderComponent } from '../../../shared/skeleton-loader/skeleton-loader.component';

@Component({
  selector: 'stupa-event-map-view',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, CapitalizeTitlePipe, EmptyStateComponent, SkeletonLoaderComponent],
  providers: [DatePipe],
  templateUrl: './event-map-view.component.html',
  styleUrl: './event-map-view.component.scss',
})
export class EventMapViewComponent implements AfterViewInit, OnDestroy {
  readonly events = input<unknown[]>([]);
  readonly origin = input<GeoPoint | null>(null);
  readonly layoutWide = input(true);
  readonly isLoading = input(false);
  readonly openEvent = output<unknown>();
  readonly registerEvent = output<unknown>();
  readonly loadMore = output<void>();
  readonly viewportChange = output<MapBounds>();

  private readonly datePipe = inject(DatePipe);
  private readonly geocode = inject(EventPlaceGeocodeService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  private readonly mapPane = viewChild<ElementRef<HTMLElement>>('mapPane');
  private resizeObserver: ResizeObserver | null = null;
  private drag: { x: number; y: number; center: GeoPoint; moved: boolean } | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinch: { distance: number; zoom: number; anchor: GeoPoint } | null = null;
  private lastTap: { time: number; x: number; y: number } | null = null;
  private consumedDoubleTap = false;
  private readonly onMapWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const local = this.panePoint(event.clientX, event.clientY);
    if (!local || event.deltaY === 0) {
      return;
    }
    this.zoomAt(local.x, local.y, this.zoom() + this.wheelZoomStep(event));
  };

  private readonly pan = signal<{
    key: string;
    center: GeoPoint;
    zoom: number;
    fromUser: boolean;
  } | null>(null);

  readonly size = signal({ width: 800, height: 640 });
  private readonly measured = signal(false);
  readonly selectedId = signal<string | null>(null);
  readonly hoveredId = signal<string | null>(null);
  readonly geocoded = signal<Record<string, GeoPoint>>({});
  readonly drawerExpanded = signal(false);
  readonly deviceOrigin = signal<GeoPoint | null>(null);
  readonly locating = signal(false);
  private drawerDrag: { y: number; moved: boolean } | null = null;
  private suppressDrawerClick = false;
  readonly legend = event_status.filter((item) => [1, 4, 13, 14].includes(item.value));

  readonly pins = computed(() => this.buildPins());
  readonly locatedPins = computed(() => this.pins().filter((pin) => pin.point));
  readonly cameraKey = computed(() => {
    const origin = this.origin();
    if (!origin) {
      return 'all';
    }
    return `near:${origin.lat}:${origin.lng}`;
  });
  readonly autoCamera = computed(() => this.buildAutoCamera());
  readonly center = computed(() => {
    const pan = this.pan();
    if (pan && pan.key === this.cameraKey()) {
      return pan.center;
    }
    return this.autoCamera().center;
  });
  readonly zoom = computed(() => {
    const pan = this.pan();
    if (pan && pan.key === this.cameraKey()) {
      return pan.zoom;
    }
    return this.autoCamera().zoom;
  });
  readonly userMoved = computed(() => {
    const pan = this.pan();
    return !!pan && pan.fromUser && pan.key === this.cameraKey();
  });
  readonly visiblePins = computed(() => this.filterVisible(this.locatedPins()));
  readonly clusters = computed(() => {
    const pins = this.visiblePins().map((pin) => ({
      id: pin.card.id,
      point: pin.point as GeoPoint,
    }));
    if (this.origin()) {
      return pins.map((pin) => ({
        id: pin.id,
        point: pin.point,
        memberIds: [pin.id],
      }));
    }
    return clusterPins(pins, this.zoom());
  });
  readonly tiles = computed(() => osmTiles(this.viewport()));
  readonly tileScale = computed(() => osmTileScale(this.zoom()));
  readonly scaleBar = computed(() => mapScale(this.center().lat, this.zoom()));
  readonly youAreHere = computed(() => this.deviceOrigin() ?? this.origin());
  readonly emptyViewport = computed(
    () =>
      this.userMoved() &&
      this.locatedPins().length > 0 &&
      this.visiblePins().length === 0,
  );
  /** Side list and the phone drawer follow the frame, so zooming out adds events. */
  readonly listPins = computed(() => this.visiblePins());
  readonly selectedPin = computed(() => {
    const id = this.selectedId();
    return this.pins().find((pin) => pin.card.id === id) ?? null;
  });

  constructor() {
    toObservable(this.events)
      .pipe(
        switchMap((list) => this.geocodeJobs(Array.isArray(list) ? list : [])),
        takeUntilDestroyed(),
      )
      .subscribe(({ job, point }) => {
        if (!point) {
          return;
        }
        this.geocoded.update((current) => ({
          ...current,
          [job.id]: point,
          [job.address.toLowerCase()]: point,
        }));
      });

    toObservable(this.origin)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.pan.set(null);
      });

    toObservable(this.queryFrame)
      .pipe(
        filter((bounds): bounds is MapBounds => bounds != null),
        debounceTime(400),
        distinctUntilChanged(
          (left, right) =>
            left.south === right.south &&
            left.west === right.west &&
            left.north === right.north &&
            left.east === right.east,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((bounds) => {
        // Hold the camera so the events response cannot move the frame and refetch.
        if (!this.pan()) {
          this.pan.set({
            key: this.cameraKey(),
            center: this.center(),
            zoom: this.zoom(),
            fromUser: false,
          });
        }
        this.viewportChange.emit(bounds);
      });
  }

  /** Settled map frame. Null until the pane has been measured. */
  private readonly queryFrame = computed(() => {
    if (!this.measured()) {
      return null;
    }
    return queryBounds(viewportBounds(this.viewport()));
  });

  ngAfterViewInit(): void {
    const pane = this.mapPane()?.nativeElement;
    if (!pane) {
      return;
    }
    pane.addEventListener('wheel', this.onMapWheel, { passive: false });
    this.measure(pane);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.measure(pane));
    this.resizeObserver.observe(pane);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mapPane()?.nativeElement.removeEventListener('wheel', this.onMapWheel);
  }

  pinStyle(point: GeoPoint): { left: string; top: string } {
    const pos = pinScreenPosition(point, {
      width: this.size().width,
      height: this.size().height,
      zoom: this.zoom(),
      center: this.center(),
    });
    return { left: `${pos.left}px`, top: `${pos.top}px` };
  }

  toneClass(statusId: unknown): string {
    return mapPinTone(statusId);
  }

  clusterTone(memberIds: readonly string[]): string {
    const first = this.pins().find((pin) => pin.card.id === memberIds[0]);
    return mapPinTone(first?.card.statusId);
  }

  clusterPinClass(memberIds: readonly string[]): string {
    const active = this.memberSelected(memberIds) ? ' is-active' : '';
    const labeled = this.origin() ? ' event-map-pin--labeled' : '';
    return `event-map-pin is-${this.clusterTone(memberIds)}${active}${labeled}`;
  }

  clusterCaption(memberIds: readonly string[]): string {
    const pin = this.pins().find((item) => item.card.id === memberIds[0]);
    if (!pin) {
      return '';
    }
    if (pin.distanceKm == null) {
      return pin.card.name;
    }
    return `${pin.card.name} - ${pin.distanceKm} km`;
  }

  memberSelected(memberIds: readonly string[]): boolean {
    const selected = this.selectedId();
    const hovered = this.hoveredId();
    return memberIds.some((id) => id === selected || id === hovered);
  }

  onClusterClick(memberIds: readonly string[]): void {
    if (memberIds.length > 1) {
      const pin = this.pins().find((item) => item.card.id === memberIds[0]);
      if (pin?.point) {
        this.applyCamera(pin.point, this.zoom() + 1, false);
      }
      return;
    }
    const id = memberIds[0] ?? null;
    this.selectPin(this.selectedId() === id ? null : id);
  }

  onPointerDown(event: PointerEvent): void {
    this.consumedDoubleTap = false;
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('.event-map-pop')) {
      return;
    }
    const local = this.panePoint(event.clientX, event.clientY);
    if (!local) {
      return;
    }
    this.pointers.set(event.pointerId, local);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    if (this.pointers.size >= 2) {
      this.beginPinch();
      return;
    }
    this.drag = { x: event.clientX, y: event.clientY, center: this.center(), moved: false };
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) {
      return;
    }
    const local = this.panePoint(event.clientX, event.clientY);
    if (!local) {
      return;
    }
    this.pointers.set(event.pointerId, local);
    if (this.pinch) {
      this.movePinch();
      return;
    }
    if (!this.drag) {
      return;
    }
    const dx = event.clientX - this.drag.x;
    const dy = event.clientY - this.drag.y;
    if (dx * dx + dy * dy < 64) {
      return;
    }
    this.drag.moved = true;
    const world = this.drag.center;
    const n = 2 ** this.zoom();
    const lng = world.lng - (dx / 256) * (360 / n);
    const latShift = (dy / 256) * (360 / n);
    this.applyCamera(
      {
        lat: Math.min(85, Math.max(-85, world.lat + latShift)),
        lng,
      },
      this.zoom(),
      true,
    );
  }

  onPointerUp(event: PointerEvent): void {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) {
      this.pinch = null;
    }
    if (this.pointers.size > 0) {
      this.drag = null;
      this.lastTap = null;
      return;
    }
    const tapped = this.drag != null && !this.drag.moved;
    this.drag = null;
    if (tapped) {
      this.onMapTap(event);
      return;
    }
    this.lastTap = null;
  }

  onDrawerPointerDown(event: PointerEvent): void {
    this.drawerDrag = { y: event.clientY, moved: false };
    const handle = event.currentTarget;
    if (handle instanceof HTMLElement) {
      handle.setPointerCapture(event.pointerId);
    }
  }

  onDrawerPointerMove(event: PointerEvent): void {
    if (!this.drawerDrag) {
      return;
    }
    const delta = event.clientY - this.drawerDrag.y;
    if (Math.abs(delta) < 8) {
      return;
    }
    this.drawerDrag.moved = true;
    if (delta < -30) {
      this.drawerExpanded.set(true);
      return;
    }
    if (delta > 30) {
      this.drawerExpanded.set(false);
    }
  }

  onDrawerPointerUp(): void {
    this.suppressDrawerClick = this.drawerDrag?.moved ?? false;
    this.drawerDrag = null;
  }

  onDrawerToggle(): void {
    if (this.suppressDrawerClick) {
      this.suppressDrawerClick = false;
      return;
    }
    this.drawerExpanded.update((open) => !open);
  }

  zoomBy(delta: number, markMoved = true): void {
    this.applyCamera(this.center(), this.zoom() + delta, markMoved);
  }

  resetView(): void {
    this.pan.set(null);
  }

  locateUser(): void {
    const geo = globalThis.navigator?.geolocation;
    if (!geo || this.locating()) {
      if (!geo) {
        this.snackBar.open(this.translate.instant('location_unavailable'), undefined, {
          duration: 4000,
        });
      }
      return;
    }
    this.locating.set(true);
    geo.getCurrentPosition(
      (position) => {
        const point = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.deviceOrigin.set(point);
        this.locating.set(false);
        this.applyCamera(point, 14, true);
      },
      () => {
        this.locating.set(false);
        this.snackBar.open(this.translate.instant('location_unavailable'), undefined, {
          duration: 4000,
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }

  selectPin(id: string | null): void {
    this.selectedId.set(id);
  }

  hoverPin(id: string | null): void {
    this.hoveredId.set(id);
  }

  onCardActivate(pin: MappedEventPin): void {
    this.selectedId.set(pin.card.id);
    if (pin.point) {
      this.applyCamera(pin.point, this.zoom(), false);
    }
  }

  onCta(pin: MappedEventPin): void {
    const event = this.rawEvent(pin.card.id);
    if (!event) {
      return;
    }
    if (Number(pin.card.statusId) === 1) {
      this.registerEvent.emit(event);
      return;
    }
    this.openEvent.emit(event);
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.85) {
      this.loadMore.emit();
    }
  }

  onDoubleClick(event: MouseEvent): void {
    if (this.consumedDoubleTap) {
      this.consumedDoubleTap = false;
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('button') || target.closest('.event-map-pop')) {
      return;
    }
    const local = this.panePoint(event.clientX, event.clientY);
    if (!local) {
      return;
    }
    this.lastTap = null;
    this.zoomAt(local.x, local.y, this.zoom() + 1);
  }

  private onMapTap(event: PointerEvent): void {
    if (!this.layoutWide() && this.drawerExpanded()) {
      this.drawerExpanded.set(false);
    }
    this.selectPin(null);
    const local = this.panePoint(event.clientX, event.clientY);
    if (!local) {
      this.lastTap = null;
      return;
    }
    if (event.pointerType !== 'mouse' && this.isDoubleTap(local, event.timeStamp)) {
      this.lastTap = null;
      this.consumedDoubleTap = true;
      this.zoomAt(local.x, local.y, this.zoom() + 1);
      return;
    }
    this.lastTap = { time: event.timeStamp, x: local.x, y: local.y };
  }

  private isDoubleTap(point: { x: number; y: number }, time: number): boolean {
    const previous = this.lastTap;
    if (!previous) {
      return false;
    }
    const dx = point.x - previous.x;
    const dy = point.y - previous.y;
    return time - previous.time <= DOUBLE_TAP_MS && dx * dx + dy * dy <= DOUBLE_TAP_SLOP_SQ;
  }

  private beginPinch(): void {
    const span = this.pointerSpan();
    if (!span) {
      return;
    }
    this.drag = null;
    this.pinch = {
      distance: span.distance,
      zoom: this.zoom(),
      anchor: pointAtScreen(this.viewport(), span.midX, span.midY),
    };
  }

  private movePinch(): void {
    const span = this.pointerSpan();
    if (!span || !this.pinch || this.pinch.distance < 1) {
      return;
    }
    const next = clampMapZoom(this.pinch.zoom + Math.log2(span.distance / this.pinch.distance));
    const center = centerForPoint(this.pinch.anchor, this.viewport(), span.midX, span.midY, next);
    this.applyCamera(center, next, true);
  }

  private pointerSpan(): { distance: number; midX: number; midY: number } | null {
    const points = Array.from(this.pointers.values());
    const first = points[0];
    const second = points[1];
    if (!first || !second) {
      return null;
    }
    return {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      midX: (first.x + second.x) / 2,
      midY: (first.y + second.y) / 2,
    };
  }

  private zoomAt(x: number, y: number, nextZoom: number): void {
    const zoom = clampMapZoom(nextZoom);
    if (zoom === this.zoom()) {
      return;
    }
    const center = zoomToward(
      {
        width: this.size().width,
        height: this.size().height,
        zoom: this.zoom(),
        center: this.center(),
      },
      x,
      y,
      zoom,
    );
    this.applyCamera(center, zoom, true);
  }

  private viewport(): MapViewport {
    return {
      width: this.size().width,
      height: this.size().height,
      zoom: this.zoom(),
      center: this.center(),
    };
  }

  private panePoint(clientX: number, clientY: number): { x: number; y: number } | null {
    const pane = this.mapPane()?.nativeElement;
    if (!pane) {
      return null;
    }
    const rect = pane.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private wheelZoomStep(event: WheelEvent): number {
    const pixels = -event.deltaY * this.wheelPixels(event.deltaMode);
    if (event.ctrlKey || event.metaKey) {
      return pixels / PINCH_ZOOM_DIVISOR;
    }
    const step = pixels / SCROLL_ZOOM_DIVISOR;
    return Math.min(SCROLL_ZOOM_STEP_CAP, Math.max(-SCROLL_ZOOM_STEP_CAP, step));
  }

  private wheelPixels(mode: number): number {
    if (mode === WheelEvent.DOM_DELTA_LINE) {
      return 16;
    }
    if (mode === WheelEvent.DOM_DELTA_PAGE) {
      return 400;
    }
    return 1;
  }

  private applyCamera(center: GeoPoint, zoom: number, fromUser: boolean): void {
    this.pan.set({
      key: this.cameraKey(),
      center,
      zoom: clampMapZoom(zoom),
      fromUser,
    });
  }

  private buildAutoCamera(): { center: GeoPoint; zoom: number } {
    const origin = this.origin();
    const points = this.locatedPins()
      .map((pin) => pin.point)
      .filter((point): point is GeoPoint => !!point);
    if (origin) {
      return nearMeCamera(origin, points, 800, 640);
    }
    if (points.length === 0) {
      return { center: { lat: 22, lng: 78 }, zoom: 5 };
    }
    return {
      center: fitCenter(points),
      zoom: points.length === 1 ? 10 : 5,
    };
  }

  private measure(pane: HTMLElement): void {
    const rect = pane.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (width <= 0 || height <= 0) {
      return;
    }
    this.measured.set(true);
    const prev = this.size();
    if (Math.abs(prev.width - width) < 8 && Math.abs(prev.height - height) < 8) {
      return;
    }
    this.size.set({ width, height });
  }

  private buildPins(): MappedEventPin[] {
    const geocoded = this.geocoded();
    const origin = this.deviceOrigin() ?? this.origin();
    const pins = this.eventList()
      .map((event) => mapEventToPin(event, this.datePipe))
      .filter((pin): pin is MappedEventPin => !!pin)
      .map((pin) => {
        const point =
          pin.point ?? geocoded[pin.card.id] ?? geocoded[pin.address.toLowerCase()] ?? null;
        return {
          ...pin,
          point,
          distanceKm: origin && point ? Math.round(distanceKm(origin, point)) : null,
        };
      });
    if (!origin) {
      return pins;
    }
    return [...pins].sort((left, right) => {
      const leftKm = left.distanceKm ?? Number.POSITIVE_INFINITY;
      const rightKm = right.distanceKm ?? Number.POSITIVE_INFINITY;
      return leftKm - rightKm;
    });
  }

  private filterVisible(pins: MappedEventPin[]): MappedEventPin[] {
    const bounds = viewportBounds({
      width: this.size().width,
      height: this.size().height,
      zoom: this.zoom(),
      center: this.center(),
    });
    return pins.filter((pin) => pin.point && pointInBounds(pin.point, bounds));
  }

  private eventList(): unknown[] {
    const events = this.events();
    return Array.isArray(events) ? events : [];
  }

  private rawEvent(id: string): unknown {
    return this.eventList().find((event) => {
      if (!event || typeof event !== 'object') {
        return false;
      }
      return String((event as { id?: unknown }).id) === id;
    });
  }

  private geocodeJobs(list: unknown[]) {
    const jobs: { id: string; address: string }[] = [];
    const seen = new Set<string>();
    for (const event of list) {
      if (hasEventVenues(event)) {
        continue;
      }
      const pin = mapEventToPin(event, this.datePipe);
      if (!pin || pin.point || !pin.address) {
        continue;
      }
      const key = pin.address.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      jobs.push({ id: pin.card.id, address: pin.address });
    }
    return from(jobs).pipe(
      concatMap((job, index) =>
        timer(index === 0 ? 0 : 1100).pipe(
          switchMap(() =>
            this.geocode.geocode(job.address).pipe(map((point) => ({ job, point }))),
          ),
        ),
      ),
    );
  }
}

function hasEventVenues(event: unknown): boolean {
  if (!event || typeof event !== 'object') {
    return false;
  }
  const venues = (event as { event_venues?: unknown }).event_venues;
  return Array.isArray(venues) && venues.length > 0;
}
